// MARK: used in v2
import { Cart, CartItem } from "@yoco/shop-sdk/lib/types"
import { navigate } from "gatsby"
import { useStoryblokApi } from "gatsby-source-storyblok"
import _differenceBy from "lodash/differenceBy"
import _filter from "lodash/filter"
import _uniqBy from "lodash/uniqBy"
import { useCallback, useContext, useRef, useState } from "react"

import { PaymentMethod } from "./constants"
import { CheckoutContext } from "./store"
import { useInitCart } from "../cart/hooks"
import { CartContext } from "../cart/store"
import { NotificationContext } from "../notifications/context"
import {
  shop,
  clearCart,
  fetchCartId,
  fetchCartProducts,
  fetchCartPromoCode,
  storeCartPromoCode,
  clearCartPromoCode,
  StoredBundle,
  StoredProduct,
  StoredItem,
  isStoredProduct,
  sessionStorageAvailable,
} from "../utils/shop"

import { useGetBundleByCode } from "@hooks/bundles"
import { useGetProductBySku } from "@hooks/products"
import { trackSegmentEvent } from "@utils/analytics"
import { withPrefix, getSignupQueryString } from "@utils/url"

export const useIsCurrentStep = (): ((
  stepName: YocoCom.CheckoutName
) => boolean) => {
  const checkoutContext = useContext(CheckoutContext)

  const isCurrentStep = useCallback(
    (stepName: YocoCom.CheckoutName) => {
      let currentStep = "Information"

      if (
        checkoutContext.contactInfo &&
        !!checkoutContext.contactInfo?.acceptTerms
      ) {
        currentStep = "Shipping"
      }

      if (
        checkoutContext.address &&
        !!checkoutContext.contactInfo?.acceptTerms
      ) {
        currentStep = "Payment"
      }

      if (checkoutContext.paidWithMethod) {
        currentStep = "Get paid"
      }

      return currentStep == stepName
    },
    [checkoutContext]
  )

  return isCurrentStep
}

export const useIsStepCompleted = (): ((
  stepName: YocoCom.CheckoutName
) => boolean) => {
  const checkoutContext = useContext(CheckoutContext)

  const isStepCompleted = useCallback(
    (stepName: YocoCom.CheckoutName) => {
      switch (stepName) {
        case "Information":
          if (checkoutContext.contactInfo)
            return Object.values(checkoutContext.contactInfo).every(
              (value) => value
            )
          else return false
        case "Shipping":
          return (
            !!checkoutContext.address &&
            !!checkoutContext.contactInfo?.acceptTerms
          )
        case "Payment":
          return (
            !!checkoutContext.paidWithMethod &&
            !!checkoutContext.contactInfo?.acceptTerms
          )
        case "Get paid":
          return false
        default:
          console.warn("failed to check if step is completed. unknown step")
          return false
      }
    },
    [checkoutContext]
  )

  return isStepCompleted
}

export const usePrepopulateCheckout = () => {
  const checkoutContext = useContext(CheckoutContext)

  const prePopulateCheckout = async (cart: Cart) => {
    if (cart.shipping_email) {
      checkoutContext.setContactInfo({
        firstName: cart.shipping_first_name,
        lastName: cart.shipping_last_name,
        email: cart.shipping_email,
        mobileNumber: cart.shipping_mobile_number.replace("+", ""),
        acceptTerms: false,
      })
    }

    if (cart.shipping_address_line1) {
      checkoutContext.setAddress({
        street: cart.shipping_address_line1,
        suburb: cart.shipping_suburb,
        city: cart.shipping_city,
        province: cart.shipping_province as YocoCom.Province,
        country: cart.shipping_country as YocoCom.Country,
        postalCode: cart.shipping_postal_code,
        longitude: cart.shipping_latitude,
        latitude: cart.shipping_latitude,
      })
    }

    checkoutContext.setRemoteCart(cart)
    if (cart.order_number) {
      checkoutContext.setOrderNumber(cart.order_number)
    }
  }
  return prePopulateCheckout
}

export const useCheckShopLive = () => {
  const storyblokApi = useStoryblokApi()
  // Use this function sparingly. Consider using isShopEnabled as an alternative

  const checkShopLive = async () => {
    const isShopLiveSetting = await storyblokApi.get(
      `cdn/stories/settings/shop${process.env.GATSBY_PREFIX_PATH}/shop-setting/`
    )
    if (!isShopLiveSetting?.data?.story?.content.isEnabled) {
      location.href = "/"
    }
  }

  return checkShopLive
}

const useSyncCartItems = () => {
  const getProduct = useGetProductBySku()
  const getBundle = useGetBundleByCode()
  const checkoutContext = useContext(CheckoutContext)
  const itemsToAdd = useRef<
    {
      itemID: number
      quantity: number
      isBundle: boolean
    }[]
  >([])

  const syncCartItems = useCallback(
    async (
      cartId: string,
      localCart: StoredItem[],
      initialRemoteCart: Cart
    ) => {
      for (const storedItem of localCart) {
        if (isStoredProduct(storedItem)) {
          await syncCartProduct(cartId, storedItem, initialRemoteCart)
        } else {
          await syncCartBundle(cartId, storedItem, initialRemoteCart)
        }
      }
      if (itemsToAdd.current.length > 0) {
        const response = await shop.carts.bulkAdd(cartId, itemsToAdd.current)
        checkoutContext.setRemoteCart(response)
        itemsToAdd.current = []
      }
    },
    []
  )

  const syncCartProduct = useCallback(
    async (cartId: string, product: StoredProduct, initialRemoteCart: Cart) => {
      const sdkProduct = getProduct(product.sku, product.storefront)

      if (!sdkProduct) {
        console.error(`Failed to get product with sku: ${product.sku}`)
        throw new Error()
      }

      const inRemoteCart = initialRemoteCart.items.find((item) => {
        return item.product.sku === product.sku && !item.bundle
      })

      if (!inRemoteCart) {
        itemsToAdd.current.push({
          itemID: parseInt(sdkProduct.externalId),
          quantity: product.quantity,
          isBundle: false,
        })
      } else if (inRemoteCart.quantity !== product.quantity) {
        if (inRemoteCart.quantity > product.quantity) {
          const response = await shop.carts.removeProduct(
            cartId,
            inRemoteCart.product.id,
            inRemoteCart.quantity - product.quantity
          )
          checkoutContext.setRemoteCart(response)
        } else {
          itemsToAdd.current.push({
            itemID: parseInt(sdkProduct.externalId),
            quantity: product.quantity - inRemoteCart.quantity,
            isBundle: false,
          })
        }
      }
    },
    [checkoutContext, getProduct]
  )

  const syncCartBundle = useCallback(
    async (cartId: string, bundle: StoredBundle, initialRemoteCart: Cart) => {
      const sdkBundle = getBundle(bundle.bundleCode)

      if (!sdkBundle) {
        console.error(`Failed to get bundle with code: ${bundle.bundleCode}`)
        throw new Error()
      }

      const inRemoteCart = initialRemoteCart.items.find((item) => {
        return item.bundle?.bundle_code === bundle.bundleCode
      })

      if (!inRemoteCart) {
        itemsToAdd.current.push({
          itemID: parseInt(sdkBundle.externalId),
          quantity: bundle.quantity,
          isBundle: true,
        })
      } else if (inRemoteCart.quantity !== bundle.quantity) {
        if (inRemoteCart.quantity > bundle.quantity) {
          const response = await shop.carts.removeBundle(
            cartId,
            parseInt(sdkBundle.externalId),
            inRemoteCart.quantity - bundle.quantity
          )

          checkoutContext.setRemoteCart(response)
        } else {
          itemsToAdd.current.push({
            itemID: parseInt(sdkBundle.externalId),
            quantity: bundle.quantity - inRemoteCart.quantity,
            isBundle: true,
          })
        }
      }
    },
    [getBundle]
  )

  return syncCartItems
}

export const useClearExistingSignup = () => {
  const notificationContext = useContext(NotificationContext)
  const cartContext = useContext(CartContext)
  const checkoutContext = useContext(CheckoutContext)

  const initCart = useInitCart()

  const handleError = useCallback(() => {
    notificationContext.setNotification({
      type: "danger",
      title: "Request failed",
      message: `Oops! Please try again later.`,
    })

    return false
  }, [notificationContext])

  const clearExistingSignup = useCallback(async () => {
    const cartId = fetchCartId()
    if (!cartId) {
      console.error(`Failed to sync cart with invalid ID: ${cartId}`)
      navigate(withPrefix("/"))
      return handleError()
    }

    try {
      const initialRemoteCart = await shop.carts.get(cartId)
      if (initialRemoteCart.is_signup_created) {
        const newCart = await shop.carts.clearSignup(cartId)
        if (newCart) {
          checkoutContext.setRemoteCart(newCart)
        }
      }
      return true
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        clearCart()
        cartContext.resetCart()
        navigate(withPrefix("/"))
      }
      return handleError()
    }
  }, [handleError, initCart, checkoutContext, cartContext])

  return clearExistingSignup
}

export const useSyncCart = (): ((
  shouldPrepopulateCheckout?: boolean
) => Promise<boolean>) => {
  const notificationContext = useContext(NotificationContext)
  const cartContext = useContext(CartContext)
  const syncCartItems = useSyncCartItems()
  const prePopulateCheckout = usePrepopulateCheckout()
  const clearRemoteCart = useClearRemoteCart()
  const checkoutContext = useContext(CheckoutContext)

  //TODO get updated designs for error notifications
  const handleError = useCallback(() => {
    notificationContext.setNotification({
      type: "danger",
      title: "Request failed",
      message: `Oops! Please try again later.`,
    })

    return false
  }, [notificationContext])

  const clearRemovedCartItems = useCallback(
    async (
      cartId: string,
      localCart: StoredItem[],
      initialRemoteCart: Cart
    ) => {
      //CLEAR PRODUCTS
      const remoteProductsCart = _filter(
        initialRemoteCart.items,
        (item: CartItem) => {
          return !item.bundle
        }
      ).map((item: CartItem) => {
        return {
          sku: item.product.sku,
          quantity: item.quantity,
          productId: item.product.id,
        }
      })

      let updatedRemoteCart: YocoCom.RemoteCart | undefined = undefined

      const differencesCartProducts = _differenceBy(
        remoteProductsCart,
        localCart,
        "sku"
      )
      for (const product of differencesCartProducts) {
        if (product.sku) {
          updatedRemoteCart = await shop.carts.removeProduct(
            cartId,
            product.productId,
            product.quantity
          )
        }
      }

      //CLEAR BUNDLES
      const remoteBundleCart = initialRemoteCart.items.map((item) => {
        return {
          bundleCode: item.bundle?.bundle_code,
          quantity: item.quantity,
          id: item.bundle?.id.toString(),
        }
      })

      const reducedBundlesCart = _uniqBy(
        remoteBundleCart,
        (bundle) => bundle.bundleCode
      )

      const differencesCart = _differenceBy(
        reducedBundlesCart,
        localCart,
        "bundleCode"
      )
      for (const bundle of differencesCart) {
        if (bundle.id) {
          updatedRemoteCart = await shop.carts.removeBundle(
            cartId,
            parseInt(bundle.id),
            bundle.quantity
          )
        }
      }
      if (updatedRemoteCart) {
        checkoutContext.setRemoteCart(updatedRemoteCart)
      }
    },
    [checkoutContext]
  )

  const syncCart = useCallback(
    async (shouldPrepopulateCheckout = true) => {
      const cartId = fetchCartId()
      if (!cartId) {
        console.error(`Failed to sync cart with invalid ID: ${cartId}`)
        clearCart()
        cartContext.resetCart()
        navigate(withPrefix("/"))
        return handleError()
      }

      try {
        const initialRemoteCart = await shop.carts.get(cartId)
        const localCart = fetchCartProducts()

        if (initialRemoteCart && initialRemoteCart.is_paid) {
          clearCart()
          cartContext.resetCart()
          navigate(withPrefix("/"))
          return true
        }

        if (!localCart) {
          console.error(`Unable to sync empty cart`)
          clearCart()
          cartContext.resetCart()
          navigate(withPrefix("/"))
          return handleError()
        }

        if (cartContext.id && localCart.length < 1) {
          await clearRemoteCart()
          return true
        }

        if (shouldPrepopulateCheckout) prePopulateCheckout(initialRemoteCart)

        try {
          await syncCartItems(cartId, localCart, initialRemoteCart)
        } catch {
          return handleError()
        }

        await clearRemovedCartItems(cartId, localCart, initialRemoteCart)

        return true
      } catch (error: any) {
        if (error.response && error.response.status === 404) {
          clearCart()
          cartContext.resetCart()
          navigate(withPrefix("/"))
        }
        return handleError()
      }
    },
    [handleError, syncCartItems, prePopulateCheckout, cartContext]
  )

  return syncCart
}

export const useClearPaidCart = () => {
  const cartContext = useContext(CartContext)

  const checkPaidCart = useCallback(async () => {
    const cartId = fetchCartId()
    if (!cartId) {
      return
    }
    try {
      const initialRemoteCart = await shop.carts.get(cartId)

      if (initialRemoteCart && initialRemoteCart.is_paid) {
        clearCart()
        cartContext.resetCart()
      }
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        clearCart()
        cartContext.resetCart()
      }
    }
  }, [cartContext])

  return checkPaidCart
}

export const useUpdateShippingAddress = (): ((
  address: YocoCom.ShippingAddress
) => Promise<boolean>) => {
  const initCart = useInitCart()
  const notificationContext = useContext(NotificationContext)

  const updateShippingAddress = useCallback(
    async (address: YocoCom.ShippingAddress) => {
      const cart = await initCart()

      if (!cart) return false

      try {
        await shop.carts.update(cart.id, {
          shipping_method: "courier",
          shipping_address_line1: address.street,
          shipping_suburb: address.suburb,
          shipping_city: address.city,
          shipping_province: address.province,
          shipping_country: address.country,
          shipping_postal_code: address.postalCode,
          shipping_latitude: address.latitude,
          shipping_longitude: address.longitude,
        })
        return true
      } catch (error: any) {
        notificationContext.setNotification({
          type: "danger",
          title: "Failed to update shipping address",
          message: error?.message,
        })
        return false
      }
    },
    [initCart]
  )

  return updateShippingAddress
}

export const useValidatePromoCode = () => {
  const cartId = fetchCartId()

  const validatePromoCode = useCallback(
    async (promoCode: string) => {
      if (cartId) {
        const response = await shop.carts.validatePromoCode(cartId, promoCode)
        return response
      }
      return { valid: false, message: "Your cart is not set up correctly." }
    },
    [cartId]
  )

  return validatePromoCode
}

export const usePreApplyPromoCode = () => {
  const notificationContext = useContext(NotificationContext)
  const checkoutContext = useContext(CheckoutContext)
  const validatePromoCode = useValidatePromoCode()

  const updatePromoCode = useCallback(
    async (
      cartId: string,
      promoCode: string
    ): Promise<{
      cart?: Cart
      error?: unknown
    }> => {
      try {
        const responseData = await shop.carts.update(cartId, {
          promo_code_str: promoCode,
        })
        return { cart: responseData, error: undefined }
      } catch (error: any) {
        return {
          cart: undefined,
          error: error?.response?.data?.promo_code_str?.[0],
        }
      }
    },
    []
  )

  const preApplyPromoCode = useCallback(async () => {
    const cartPromoCode = fetchCartPromoCode()
    const cartId = fetchCartId()

    if (cartId && cartPromoCode) {
      const remoteCart = await shop.carts.get(cartId)
      const remoteCartPromoCode = remoteCart.promo_code?.code

      let updateResponse: {
        cart?: Cart
        error?: unknown
      } = {
        cart: undefined,
        error: undefined,
      }

      updateResponse = await updatePromoCode(cartId, cartPromoCode)
      if (updateResponse.cart) {
        checkoutContext.setRemoteCart(updateResponse.cart)
        notificationContext.setNotification({
          type: "success",
          title: `The promo code "${cartPromoCode}" has been applied to your cart`,
        })
      } else if (!updateResponse.cart && remoteCartPromoCode) {
        const initialError = updateResponse.error
        const { valid } = await validatePromoCode(remoteCartPromoCode)

        if (valid) {
          updateResponse.error = undefined
          storeCartPromoCode(remoteCartPromoCode)
          notificationContext.setNotification({
            type: "warning",
            title: `Promo code "${cartPromoCode}" reverted to "${remoteCartPromoCode}"`,
            message: `"${cartPromoCode}" is invalid so "${remoteCartPromoCode}" was re-applied.`,
          })
        } else {
          updateResponse.error = initialError
        }
      }

      if (updateResponse.error && typeof updateResponse.error === "string") {
        updatePromoCode(cartId, "").then((removedPromoResponse) => {
          if (removedPromoResponse.cart) {
            checkoutContext.setRemoteCart(removedPromoResponse.cart)
          }
        })
        clearCartPromoCode()
        notificationContext.setNotification({
          type: "danger",
          title: `Could not pre-set promo code "${cartPromoCode}"`,
          message: updateResponse.error.toLowerCase(),
        })
      }

      return updateResponse.cart ? true : false
    }

    return true
  }, [notificationContext, checkoutContext, updatePromoCode, validatePromoCode])

  return preApplyPromoCode
}

export const useApplyPromoCode = (): ((
  promoCode: string
) => Promise<boolean>) => {
  const initCart = useInitCart()
  const checkoutContext = useContext(CheckoutContext)
  const notificationContext = useContext(NotificationContext)

  const applyPromoCode = useCallback(
    async (promoCode: string) => {
      const cart = await initCart()

      if (!cart) return false

      try {
        const response = await shop.carts.update(cart.id, {
          promo_code_str: promoCode,
        })

        checkoutContext.setRemoteCart(response)

        if (response?.promo_code?.code) {
          notificationContext.setNotification({
            type: "success",
            title: "Promo code applied to order",
          })
        } else {
          notificationContext.setNotification({
            type: "success",
            title: "Promo code has been removed",
          })
        }
        return true
      } catch (error: any) {
        notificationContext.setNotification({
          type: "danger",
          title: "Failed to apply promotion code",
          message: (
            error?.response?.data?.promo_code_str?.[0] ?? error?.message
          ).toLowerCase(),
        })
        return false
      }
    },
    [initCart]
  )

  return applyPromoCode
}

export const useUpdateContactInfo = (): ((
  contactInfo: YocoCom.ContactInfo
) => Promise<boolean>) => {
  const initCart = useInitCart()
  const notificationContext = useContext(NotificationContext)

  const updateContactInfo = useCallback(
    async (contactInfo: YocoCom.ContactInfo) => {
      const cart = await initCart()

      if (!cart) return false

      try {
        await shop.carts.update(cart.id, {
          shipping_first_name: contactInfo.firstName,
          shipping_last_name: contactInfo.lastName,
          shipping_email: contactInfo.email,
          shipping_mobile_number: contactInfo.mobileNumber,
          has_accepted_terms: contactInfo.acceptTerms,
        })
        return true
      } catch (error: any) {
        notificationContext.setNotification({
          type: "danger",
          title: "Failed to update contact info",
          message: error?.message,
        })
        return false
      }
    },
    [initCart]
  )

  return updateContactInfo
}

export const useGenerateOrderNumber = (): (() => Promise<Cart | null>) => {
  const notificationContext = useContext(NotificationContext)

  const generateOrderNumber = useCallback(async () => {
    const cartId = fetchCartId()

    if (!cartId) return null

    try {
      const response = await shop.carts.generateOrderNumber(cartId)
      return response
    } catch (error: any) {
      // TODO - better typing for all SDK errors
      notificationContext.setNotification({
        type: "danger",
        title: "Failed to generate order number",
        message: error?.message,
      })
      throw error
    }
  }, [])

  return generateOrderNumber
}

export const useInitiateFreePayment = () => {
  const notificationContext = useContext(NotificationContext)
  const checkoutContext = useContext(CheckoutContext)

  const initiateFreePayment = useCallback(async () => {
    const cartId = fetchCartId()

    if (!cartId) return null

    try {
      const response = await shop.carts.initiateFreePayment(cartId)
      checkoutContext.setRemoteCart(response)
      checkoutContext.setOrderNumber(response.order_number)
      checkoutContext.setPaidWithMethod(PaymentMethod.Free)
      trackSegmentEvent("shop_payment_details_entered", {
        paymentMethod: PaymentMethod[PaymentMethod.Free],
      })
    } catch (error: any) {
      notificationContext.setNotification({
        type: "danger",
        title: "Failed to skip payment",
        message: error?.message,
      })
    }
  }, [])

  return initiateFreePayment
}

export const useClearRemoteCart = () => {
  const { setRemoteCart } = useContext(CheckoutContext)
  const { setNotification } = useContext(NotificationContext)

  const clearRemoteCart = useCallback(async () => {
    try {
      const cartId = fetchCartId()
      if (!cartId) return

      const initialRemoteCart = await shop.carts.get(cartId)
      if (!initialRemoteCart) return

      if (initialRemoteCart.items.length > 0) {
        let clearedCart: Cart | undefined = undefined

        clearedCart = await shop.carts.clear(cartId)

        if (clearedCart) {
          setRemoteCart(clearedCart)
        }
        return
      }
      setRemoteCart(initialRemoteCart)
    } catch (error: any) {
      setNotification({
        type: "danger",
        title: "Failed to clear remote cart",
        message: error?.message,
      })
    }
  }, [])

  return clearRemoteCart
}

export const useHandleSignupCheckout = () => {
  const syncCart = useSyncCart()
  const generateOrderNumber = useGenerateOrderNumber()
  const checkoutContext = useContext(CheckoutContext)
  const applyPromoCode = usePreApplyPromoCode()
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)

  const handleRedirect = useCallback(
    async (businessUUID?: string) => {
      setIsLoading(true)

      try {
        if (!checkoutContext.remoteCart?.order_number) {
          await generateOrderNumber()
        }

        // TODO - fix the way useSyncCart and usePreApplyPromoCode bubbles up errors
        const syncCartResponse = await syncCart()

        if (!syncCartResponse) throw new Error("Failed to sync cart")

        const applyPromoCodeResponse = await applyPromoCode()

        if (!applyPromoCodeResponse)
          throw new Error("Failed to apply promo code")

        trackSegmentEvent("shop_checkout")
        window.history.pushState("forward", "null", withPrefix("/"))
        setIsSuccessful(true)
        let URL: string
        if (process.env.GATSBY_OAUTH_REDIRECT === "true" && businessUUID) {
          URL = `${
            process.env.GATSBY_SHOP_URL
          }/business/${businessUUID}/${fetchCartId()}`

          const signupQueryString = getSignupQueryString()

          if (signupQueryString) {
            URL += `?${signupQueryString}`
          }
        } else {
          URL = `${
            process.env.GATSBY_SHOP_SIGNUP_URL
          }?userJourney=yocoshop&cartUUID=${fetchCartId()}`

          const signupQueryString = getSignupQueryString()

          if (signupQueryString) {
            URL += `&${signupQueryString}`
          }
        }
        window.location.href = URL
        setIsLoading(false)
      } catch (error) {
        console.error(`Failed to handleRedirect....${error}`)
        setIsLoading(false)
      }
    },
    [checkoutContext, generateOrderNumber, syncCart, applyPromoCode]
  )

  return { isLoading, isSuccessful, handleRedirect }
}

export const useHandleLoginCheckout = () => {
  const checkoutContext = useContext(CheckoutContext)
  const cartContext = useContext(CartContext)

  const handleLoginCheckout = useCallback(async () => {
    if (process.env.GATSBY_OAUTH_REDIRECT !== "true") {
      navigate(withPrefix("/checkout/"))
      cartContext.setCartPreviewOpen(false)
    } else {
      trackSegmentEvent("website_oauth_checkout_initialised")
      checkoutContext.setIsHandlingLogin(true)

      try {
        if (sessionStorageAvailable()) {
          const session = await shop.oauth.startSession()
          sessionStorage.setItem("csrf_token", session.csrf_token)

          const redirectUrl = await shop.oauth.getAuthUrl(session.csrf_token)
          window.location.href = redirectUrl
        }
        // We leave `isHandlingLogin` state `true` here since the page will redirect
      } catch (error) {
        console.error(`Failed to handleLoginCheckout....${error}`)
        navigate(withPrefix("/checkout/"))
        cartContext.setCartPreviewOpen(false)
        checkoutContext.setIsHandlingLogin(false)
      }
    }
  }, [checkoutContext, cartContext])

  // ToDo: we could return the `isHandlingLogin` state here but it
  // would just serve as a proxy to the state in the checkout context
  return { handleLoginCheckout }
}
